import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldAlert, ShieldCheck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useMfaStatus } from "@/hooks/useMfaStatus";
import { MFA_ENABLED } from "@/config/mfaFlags";
import { useAuth } from "@/context/Auth";

const DISMISS_KEY = "mfa_banner_dismissed_at";
const DISMISS_TTL_MS = 1000 * 60 * 60 * 8; // 8 hours

/**
 * Banner states (deadline sourced from mfa_get_my_status RPC via useMfaStatus):
 *   - "2FA required now"            (red, non-dismissible) — deadline passed
 *   - "2FA required soon"           (red, non-dismissible) — ≤ 3 days left
 *   - "MFA required in X days"      (amber, dismissible)   — > 3 days left
 *   - recommendation banner         (amber, dismissible)   — no policy applies
 *   - hidden                        — enrolled / loading / on /account/security
 */
export default function MfaStatusBanner() {
  const { session } = useAuth();
  const location = useLocation();
  const { loading, enrolled, requiredAfter } = useMfaStatus();
  const [dismissedAt, setDismissedAt] = useState<number>(() => {
    const raw = typeof window !== "undefined" ? window.sessionStorage.getItem(DISMISS_KEY) : null;
    return raw ? Number(raw) : 0;
  });

  if (!MFA_ENABLED || !session?.user || loading || enrolled) return null;
  if (location.pathname.startsWith("/account/security")) return null;

  // No enforcement → optional/recommended banner
  if (!requiredAfter) {
    if (Date.now() - dismissedAt < DISMISS_TTL_MS) return null;
    return (
      <BannerShell tone="amber" dismissible onDismiss={() => dismiss(setDismissedAt)}>
        <Title tone="amber">Protect your account with Multi-Factor Authentication (2FA)</Title>
        <Subtitle tone="amber">
          Add a second verification step using an authenticator app. Recommended for all CSV Pro users.
        </Subtitle>
      </BannerShell>
    );
  }

  const msLeft = requiredAfter.getTime() - Date.now();
  const required = msLeft <= 0;

  if (required) {
    // Required now — non-dismissible, red.
    return (
      <BannerShell tone="red" icon={<ShieldAlert className="h-4 w-4" />}>
        <Title tone="red">Multi-Factor Authentication is required now</Title>
        <Subtitle tone="red">
          Your account requires 2FA to continue. Please enable it to keep access to CSV Pro.
        </Subtitle>
      </BannerShell>
    );
  }

  const days = Math.ceil(msLeft / (24 * 3600 * 1000));

  // ≤3 days remaining → escalate to red "soon" (non-dismissible)
  if (days <= 3) {
    return (
      <BannerShell tone="red" icon={<ShieldAlert className="h-4 w-4" />}>
        <Title tone="red">2FA required soon</Title>
        <Subtitle tone="red">
          Your account will require 2FA on {requiredAfter.toLocaleDateString()} ({days} day{days === 1 ? "" : "s"} left). Enroll now to avoid interruption.
        </Subtitle>
      </BannerShell>
    );
  }

  if (Date.now() - dismissedAt < DISMISS_TTL_MS) return null;

  return (
    <BannerShell tone="amber" dismissible onDismiss={() => dismiss(setDismissedAt)}>
      <Title tone="amber">MFA required in {days} days</Title>
      <Subtitle tone="amber">
        Your account will require 2FA on {requiredAfter.toLocaleDateString()}. Enroll now to avoid interruption.
      </Subtitle>
    </BannerShell>
  );
}

function dismiss(set: (n: number) => void) {
  const now = Date.now();
  window.sessionStorage.setItem(DISMISS_KEY, String(now));
  set(now);
}

type Tone = "amber" | "red";
const tones: Record<Tone, { wrap: string; chip: string; btn: string; close: string }> = {
  amber: {
    wrap: "border-b border-amber-300 bg-amber-50 dark:border-amber-800/60 dark:bg-amber-950/40",
    chip: "bg-amber-500/15 text-amber-700 dark:text-amber-300",
    btn: "bg-amber-600 hover:bg-amber-500 text-white",
    close: "text-amber-900/70 hover:text-amber-900 dark:text-amber-200/70 dark:hover:text-amber-100",
  },
  red: {
    wrap: "border-b border-red-300 bg-red-50 dark:border-red-800/60 dark:bg-red-950/40",
    chip: "bg-red-500/15 text-red-700 dark:text-red-300",
    btn: "bg-red-600 hover:bg-red-500 text-white",
    close: "text-red-900/70",
  },
};

function BannerShell({
  tone,
  children,
  dismissible,
  onDismiss,
  icon,
}: {
  tone: Tone;
  children: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  icon?: React.ReactNode;
}) {
  const t = tones[tone];
  return (
    <div role="region" aria-label="Security recommendation" className={t.wrap}>
      <div className="container flex items-center gap-3 py-2.5">
        <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${t.chip}`}>
          {icon ?? <ShieldAlert className="h-4 w-4" />}
        </div>
        <div className="flex-1 min-w-0">{children}</div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Link to="/account/security">
            <Button size="sm" className={t.btn}>{tone === "red" ? "Enable now" : "Enable 2FA"}</Button>
          </Link>
          {dismissible && onDismiss && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={`h-8 w-8 ${t.close}`}
              onClick={onDismiss}
              aria-label="Dismiss for this session"
            >
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function Title({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  const c = tone === "red" ? "text-red-900 dark:text-red-100" : "text-amber-900 dark:text-amber-100";
  return <p className={`text-sm font-medium ${c} leading-tight`}>{children}</p>;
}
function Subtitle({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  const c = tone === "red" ? "text-red-800/80 dark:text-red-200/70" : "text-amber-800/80 dark:text-amber-200/70";
  return <p className={`text-xs ${c} leading-tight mt-0.5`}>{children}</p>;
}

// Suppress unused warning for icon import (used conditionally)
void ShieldCheck;
